import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { fetchData } from '../api/publicService';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next'; // 1. Import hook
import {
  Box, Typography, Container, CircularProgress, Alert, Grid, Paper, List,
  ListItem, ListItemIcon, ListItemText, Divider, Slide, Fade,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

// Import Leaflet components and the reusable card
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import RelatedItemCard from '../components/RelatedItemCard'; // Adjust path if needed

const DestinationDetail = () => {
  const { slug } = useParams();
  const { language } = useLanguage();
  const { t } = useTranslation(); // 2. Initialize translation function
  const [destination, setDestination] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    const loadData = async () => {
      try {
        setLoading(true);
        const data = await fetchData(`/destinations/${slug}`, language);
        setDestination(data);
      } catch (error) {
        console.error("Failed to fetch destination details", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [slug, language]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 10 }}><CircularProgress /></Box>;
  if (!destination) return <Container sx={{ py: 5 }}><Alert severity="error">{t('destination_detail_not_found')}</Alert></Container>;

  return (
    <>
      {/* SEO Head Tags */}
      <Helmet>
        <title>{destination.seoTitle}</title>
        <meta name="description" content={destination.metaDescription} />
      </Helmet>

      {/* --- Section 1: Immersive Hero --- */}
      <Box
        sx={{
          height: '70vh', position: 'relative', display: 'flex', alignItems: 'center', mt:5,
          color: 'white', '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.4)', zIndex: 1 },
        }}
      >
        <Box component="img" src={destination.images[0]} sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }} />
        <Container sx={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
          <Slide direction="down" in={!loading} timeout={800}>
            <Typography variant="h2" component="h2" sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700 }}>
              {destination.name}
            </Typography>
          </Slide>
          <Fade in={!loading} timeout={1200}>
            <Typography variant="h5" sx={{ mt: 2, maxWidth: '700px', mx: 'auto', opacity: 0.9 }}>
              {destination.shortDescription}
            </Typography>
          </Fade>
        </Container>
      </Box>

      {/* --- Section 2: Main Description --- */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Typography variant="body1" sx={{ fontSize: '1.2rem', lineHeight: 1.8, textAlign: 'center', color: 'text.secondary' }}>
          {destination.description}
        </Typography>
      </Container>

      {/* --- Section 3: Gallery & Highlights --- */}
      <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={5} alignItems="center">
            <Grid item xs={12} md={7}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Box component="img" src={destination.images[1] || destination.images[0]} sx={{ width: '100%', height: 350, objectFit: 'cover', borderRadius: 2, boxShadow: 5 }} />
                </Grid>
                <Grid item xs={6}>
                  <Box component="img" src={destination.images[2] || destination.images[0]} sx={{ width: '100%', height: 250, objectFit: 'cover', borderRadius: 2, boxShadow: 5 }} />
                </Grid>
                <Grid item xs={6}>
                  <Box component="img" src={destination.images[3] || destination.images[0]} sx={{ width: '100%', height: 250, objectFit: 'cover', borderRadius: 2, boxShadow: 5 }} />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={5}>
              <Typography variant="h4" component="h2" sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, mb: 3 }}>
                {t('destination_detail_highlights')}
              </Typography>
              <List>
                {destination.keyHighlights.map((highlight, i) => (
                  <ListItem key={i} disableGutters>
                    <ListItemIcon sx={{ minWidth: 40 }}><CheckCircleOutlineIcon color="primary" /></ListItemIcon>
                    <ListItemText primary={highlight} primaryTypographyProps={{ fontSize: '1.1rem' }} />
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* --- Section 4: Interactive Map --- */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h4" component="h2" align="center" sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, mb: 4 }}>
          {t('destination_detail_map_title')}
        </Typography>
        <Box sx={{ height: '400px', width: '100%', borderRadius: 2, overflow: 'hidden', boxShadow: 5 }}>
          <MapContainer center={[destination.latitude, destination.longitude]} zoom={13} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <Marker position={[destination.latitude, destination.longitude]}><Popup>{destination.name}</Popup></Marker>
          </MapContainer>
        </Box>
      </Container>

      {/* --- Section 5: Related Trips --- */}
      {destination.relatedTrips?.length > 0 && (
        <Box sx={{ bgcolor: 'grey.100', py: 8 }}>
          <Container maxWidth="lg">
            <Typography variant="h4" fontWeight="bold" align="center" gutterBottom>{t('destination_detail_related_trips')}</Typography>
            <Divider sx={{ my: 4, mx: 'auto', width: '100px', borderWidth: '2px' }} />
            <Grid container spacing={4}>
              {destination.relatedTrips.map(trip => (
                <Grid item key={trip._id} xs={12} sm={6} md={4}>
                  <RelatedItemCard item={trip} type="trip" />
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      )}

      {/* --- Section 6: Related Blogs --- */}
      {destination.relatedBlogs?.length > 0 && (
        <Box sx={{ py: 8 }}>
          <Container maxWidth="lg">
            <Typography variant="h4" fontWeight="bold" align="center" gutterBottom>{t('destination_detail_related_blogs')}</Typography>
            <Divider sx={{ my: 4, mx: 'auto', width: '100px', borderWidth: '2px' }} />
            <Grid container spacing={4}>
              {destination.relatedBlogs.map(blog => (
                <Grid item key={blog._id} xs={12} sm={6} md={4}>
                  <RelatedItemCard item={blog} type="blog" />
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      )}
    </>
  );
};

export default DestinationDetail;